import React, { Fragment, useEffect, useState } from 'react';
import SalesTracking from './SalesTracking/SalesTracking';
import { ScrollArea, ScrollAreaInner, Wrapper } from './styled/TrainerStyled';
import Alert from '../common/Alert';
import CallResultPopup from '../CallResultPopup/CallResultPopup';
import useLocalization from '../../localization/useLocalization';
import Transcript from '../../containers/Transcript/Transcript';
import UserInfo from '../../containers/UserInfo/UserInfo';
import CallButton from './CallButton/TrainerCallButton';
import { ScrollableContainer } from '../common/ScrollableContainer/ScrollableContainer';
import { OptInControls } from './OptOutControls/TrainerOptOutControls';
import Prompt from '../common/Prompt/Prompt';
import ConversionModal from './Conversion/TrainerConversion';
import type { ConversionPayload } from './Conversion/TrainerConversion';
import CallSuggestions from '../../containers/CallSuggestions/CallSuggestions';
import Evaluations from './Evaluations/Evaluations';
import { AudioDeviceSelector } from '../../components/Settings/AudioDevice/audioDeviceSelector';
import Modal from '../../components/Modal/Modal';
import { Translator } from './Translator/Translator';
import { AssistantChat } from './AssistantChat/AssistantChat';
import { TrainerTopPanel } from './sections/TrainerTopPanel';
import { TrainerWordDetectionSection } from './sections/TrainerWordDetectionSection';
import { TrainerTabs } from './sections/TrainerTabs';
import { TrainerOverlays } from './sections/TrainerOverlays';
import { CallResultScore, PhraseGroup, SpottingScore } from './types/detection';
import type { KeyPhraseIntegrationWords, PromptCurrentPrompt, TrainerAsrConfigErrors } from '../../store/types';
import {
    buildTrainerViewState,
    canShowAudioDeviceSelectorModal,
    canShowCallButton,
    canShowCallPopupContent,
    canShowConversionModal,
    canShowOptInControls,
    canShowTrainerRuntimeOverlays,
    isPromptVisible,
    type TrainerUserInterfaceConfig
} from './viewState';

const TAB_ASSISTANT = 'assistant';
const TAB_SALES = 'sales';
const TAB_EVALUATIONS = 'evaluations';
const TAB_TRANSLATOR = 'translator';

interface TrainerLocalization {
    getText: (key: string) => string;
}
interface TopPanelState {
    prompt: { currentPrompt?: PromptCurrentPrompt };
}

interface DetectionState {
    whitelist: PhraseGroup[];
    blacklist: PhraseGroup[];
    asrConfigErrors: TrainerAsrConfigErrors;
    optInOn: boolean;
    userInterfaceConfig: TrainerUserInterfaceConfig;
    isCallStarted: boolean;
    callHold: boolean;
    configLoaded?: boolean;
    defaultWordsRendered?: number | null;
    keyPhraseIntegrationWords?: KeyPhraseIntegrationWords | null;
}

interface TabsState {
    enableTrainer: boolean;
    enableSales: boolean;
    enableEvaluations: boolean;
    isNotificationShown: boolean;
    callEventNotification?: string | null;
}

interface OverlayState {
    isCallResultVisible: boolean;
    isLastCallResult: boolean;
    whitelistPhrasesSpottingScore: SpottingScore | null;
    lastCallPhraseSpottingScore: CallResultScore | null;
    conversionModalOpen: boolean;
    callId?: string | number | null;
    userId?: number | string | null;
    micDeviceNotAvailable: boolean;
}

interface CallResultPopupState {
    bonusScore?: number;
    spottedPhrasesScore?: number;
    totalPhrasesScore?: number;
}

interface CallControlState {
    isManualCallButtonEnabled: boolean;
    isOptOut: boolean;
    stopRecordingCustomer: boolean;
}

const getStoredBoolean = (value: string | null): boolean | undefined => {
    if (!value) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(value);
        return typeof parsed === 'boolean' ? parsed : undefined;
    } catch {
        return undefined;
    }
};

interface TrainerProps {
    localization: TrainerLocalization;
    topPanelState: TopPanelState;
    detectionState: DetectionState;
    tabsState: TabsState;
    overlayState: OverlayState;
    callControlState: CallControlState;
    callActions: CallActions;
    optActions: OptActions;
    conversionActions: ConversionActions;
    trainerActions: TrainerActions;
}

interface CallActions {
    sendManualCallEvent: (isStart: boolean) => void;
    setManualButtonDisabled: () => void;
}

interface OptActions {
    manualOptIn: () => void;
    manualOptOut: () => void;
    manualOptStop: () => void;
}

interface ConversionActions {
    saveConversion: (payload: ConversionPayload) => void;
    closeConversionModal: () => void;
}

interface TrainerActions {
    init: () => void;
    reload: () => void;
    closeResultPopup: () => void;
}

function TrainerView(props: TrainerProps) {
    const [isResultShown, setIsResultShown] = useState(false);
    const [tab, setTab] = useState('');

    const {
        localization,
        topPanelState,
        detectionState,
        tabsState,
        overlayState,
        callControlState,
        callActions,
        optActions,
        conversionActions,
        trainerActions
    } = props;

    useEffect(() => {
        trainerActions.init();
        // componentDidMount parity: run once on mount.
    }, []);

    useEffect(() => {
        if (overlayState.isCallResultVisible && !isResultShown) {
            setIsResultShown(true);
        }
    }, [overlayState.isCallResultVisible, isResultShown]);

    const isTrainerEnabled = Boolean(tabsState.enableTrainer);
    const isSalesEnabled = Boolean(tabsState.enableSales);
    const isEvaluationsEnabled = Boolean(tabsState.enableEvaluations);
    const isTranslatorEnabled = Boolean(detectionState.userInterfaceConfig.displayTranslator);
    const isAssistantChatEnabled = Boolean(
        detectionState.userInterfaceConfig.displayAssistantChat && detectionState.userInterfaceConfig.assistantChatProjectId
    );

    const tabActions = {
        onTabAssistant: () => isTrainerEnabled && setTab(TAB_ASSISTANT),
        onTabSales: () => setTab(TAB_SALES),
        onTabEvaluations: () => setTab(TAB_EVALUATIONS),
        onTabTranslator: () => setTab(TAB_TRANSLATOR)
    };

    const renderPrompt = () => {
        if (!isPromptVisible(detectionState.userInterfaceConfig, detectionState.isCallStarted)) {
            return null;
        }

        return <Prompt prompt={topPanelState.prompt.currentPrompt} />;
    };

    const renderWordDetection = () => {
        const { noWhitelist = false, noBlacklist = false } = detectionState.asrConfigErrors;

        return (
            <TrainerWordDetectionSection
                whitelist={detectionState.whitelist}
                blacklist={detectionState.blacklist}
                noWhitelist={noWhitelist}
                noBlacklist={noBlacklist}
                localization={localization}
                optInOn={detectionState.optInOn}
                isCallStarted={detectionState.isCallStarted}
                callHold={detectionState.callHold}
                userInterfaceConfig={detectionState.userInterfaceConfig}
                wordsDetectionConfig={{
                    configLoaded: detectionState.configLoaded,
                    defaultWordsRendered: detectionState.defaultWordsRendered,
                    isCallStarted: detectionState.isCallStarted,
                    keyPhraseIntegrationWords: detectionState.keyPhraseIntegrationWords,
                    sendManualCallEvent: callActions.sendManualCallEvent
                }}
            />
        );
    };

    const renderCallPopup = () => {
        const wasOptInPressed = getStoredBoolean(localStorage.getItem('wasOptInPressed'));

        const isVisible = canShowCallPopupContent({
            isResultShown,
            isCallResultVisible: overlayState.isCallResultVisible,
            optInOn: detectionState.optInOn,
            displayCallOptOutControls: detectionState.userInterfaceConfig.displayCallOptOutControls,
            wasOptInPressed
        });

        if (!isVisible && !isResultShown) {
            return null;
        }

        const activeScore: CallResultPopupState | null = overlayState.isLastCallResult
            ? overlayState.lastCallPhraseSpottingScore
            : overlayState.whitelistPhrasesSpottingScore;

        return (
            <CallResultPopup
                bonusScore={activeScore?.bonusScore}
                spottedPhrasesScore={activeScore?.spottedPhrasesScore}
                totalPhrasesScore={activeScore?.totalPhrasesScore}
                isVisible={isVisible}
                onClose={trainerActions.closeResultPopup}
                isLastCallResult={overlayState.isLastCallResult}
            />
        );
    };

    const renderCallButton = () => {
        if (!canShowCallButton(detectionState.userInterfaceConfig.explicitIntegrationSource)) {
            return null;
        }

        return (
            <CallButton
                sendManualCallEvent={callActions.sendManualCallEvent}
                isCallStarted={detectionState.isCallStarted}
                isManualCallButtonEnabled={callControlState.isManualCallButtonEnabled}
                setDisabled={callActions.setManualButtonDisabled}
                localization={localization}
            />
        );
    };

    const renderCallOptInControls = () => {
        const displayCallOptOutControls = detectionState.userInterfaceConfig.displayCallOptOutControls ?? false;

        const manualOptInCb = () => {
            localStorage.setItem('wasOptInPressed', 'true');
            optActions.manualOptIn();
        };

        if (canShowOptInControls(displayCallOptOutControls)) {
            return (
                <OptInControls
                    optInOn={detectionState.optInOn}
                    isOptOut={callControlState.isOptOut}
                    stopRecordingCustomer={callControlState.stopRecordingCustomer}
                    localization={localization}
                    manualOptIn={manualOptInCb}
                    manualOptOut={optActions.manualOptOut}
                    manualOptStop={optActions.manualOptStop}
                    isCallStarted={detectionState.isCallStarted}
                />
            );
        }
        return null;
    };

    const renderConversionModal = () => {
        if (
            !canShowConversionModal(
                overlayState.conversionModalOpen,
                detectionState.userInterfaceConfig,
                overlayState.callId,
                overlayState.userId
            )
        ) {
            return null;
        }
        if (!overlayState.callId || overlayState.userId == null) {
            return null;
        }

        const safeCallId = String(overlayState.callId);
        const safeUserId = Number(overlayState.userId);
        if (!Number.isFinite(safeUserId)) {
            return null;
        }

        return (
            <ConversionModal
                onFinish={conversionActions.closeConversionModal}
                saveConversion={conversionActions.saveConversion}
                callId={safeCallId}
                userId={safeUserId}
                localization={localization}
            />
        );
    };

    const renderAudioDeviceSelectorModal = () => {
        if (canShowAudioDeviceSelectorModal(overlayState.micDeviceNotAvailable)) {
            return (
                <Modal title={localization.getText('settings.inputDevice.title')}>
                    <Alert severity="warning">{localization.getText('settings.inputDevice.noFound')}</Alert>
                    <hr style={{ margin: '16px 0 0 0', border: 'none' }} />
                    <AudioDeviceSelector localization={localization} />
                </Modal>
            );
        }
        return null;
    };

    const { tabsEnabled, selectedTab, displayRealTimeHints } = buildTrainerViewState({
        tab,
        isTrainerEnabled,
        isSalesEnabled,
        isEvaluationsEnabled,
        isTranslatorEnabled,
        userInterfaceConfig: detectionState.userInterfaceConfig,
        tabAssistant: TAB_ASSISTANT,
        tabSales: TAB_SALES
    });

    return (
        <Wrapper data-testid="Trainer">
            <Fragment>
                <Transcript />
                {renderCallOptInControls()}
                <ScrollArea>
                    <ScrollAreaInner>
                        <ScrollableContainer>
                            <TrainerTopPanel suggestions={<CallSuggestions />} prompt={renderPrompt()} />
                            <TrainerTabs
                                tabsEnabled={tabsEnabled}
                                selectedTab={selectedTab}
                                tabAssistant={TAB_ASSISTANT}
                                tabSales={TAB_SALES}
                                tabEvaluations={TAB_EVALUATIONS}
                                tabTranslator={TAB_TRANSLATOR}
                                isTrainerEnabled={isTrainerEnabled}
                                isSalesEnabled={isSalesEnabled}
                                isEvaluationsEnabled={isEvaluationsEnabled}
                                isTranslatorEnabled={isTranslatorEnabled}
                                localization={localization}
                                isNotificationShown={tabsState.isNotificationShown}
                                callEventNotification={tabsState.callEventNotification}
                                tabActions={tabActions}
                                assistantPanel={renderWordDetection()}
                                salesPanel={<SalesTracking />}
                                evaluationsPanel={<Evaluations />}
                                translatorPanel={<Translator localization={localization} />}
                            />

                            {canShowTrainerRuntimeOverlays(isTrainerEnabled) && (
                                <TrainerOverlays
                                    displayRealTimeHints={displayRealTimeHints}
                                    callPopup={renderCallPopup()}
                                    callButton={renderCallButton()}
                                    conversionModal={renderConversionModal()}
                                    audioDeviceSelectorModal={renderAudioDeviceSelectorModal()}
                                />
                            )}

                            <div data-testid="trainer-user-info-section">
                                <UserInfo />
                            </div>
                        </ScrollableContainer>
                    </ScrollAreaInner>
                </ScrollArea>
                {isAssistantChatEnabled && (
                    <AssistantChat
                        localization={localization}
                        projectId={String(detectionState.userInterfaceConfig.assistantChatProjectId || '')}
                    />
                )}
            </Fragment>
        </Wrapper>
    );
}

function Trainer(props: Omit<TrainerProps, 'localization'>) {
    const localization = useLocalization();
    return <TrainerView {...props} localization={localization} />;
}

export default Trainer;
